/**
 * UI operations for popup custom module
 * @module popup-custom/ui
 */

import { SELECTORS, FOCUSABLE } from './constants.js';
import { markSeen } from './storage.js';

let _savedBodyOverflow = '';
type PopupDialog = HTMLDialogElement & {
  _storageKey?: string;
  _keydownHandler?: (event: KeyboardEvent) => void;
};

/**
 * Shows the popup dialog with scroll locking
 * @param {HTMLDialogElement} dialog - The dialog element
 * @param {string} storageKey - The localStorage key for this popup instance
 */
export function openPopup(dialog: HTMLDialogElement, storageKey?: string): void {
  if (dialog.open) return;

  const popup = dialog as PopupDialog;

  _savedBodyOverflow = document.body.style.overflow;
  document.body.style.overflow = 'hidden';
  dialog.show();
  popup._storageKey = storageKey;

  const inner = dialog.querySelector<HTMLElement>('.popup-custom__inner');
  if (inner) inner.focus();

  if (popup._keydownHandler) {
    document.addEventListener('keydown', popup._keydownHandler);
  }
}

/**
 * Hides the popup dialog and restores scrolling
 * @param {HTMLDialogElement} dialog - The dialog element
 */
export function closePopup(dialog: HTMLDialogElement): void {
  const popup = dialog as PopupDialog;
  if (popup._storageKey) {
    markSeen(popup._storageKey);
  }
  if (popup._keydownHandler) {
    document.removeEventListener('keydown', popup._keydownHandler);
  }
  dialog.close();
  document.body.style.overflow = _savedBodyOverflow;
}

/**
 * Hides the popup dialog without marking it as seen.
 * Used in Shopify theme editor where close is a preview action.
 * @param {HTMLDialogElement} dialog - The dialog element
 */
export function closePopupPreview(dialog: HTMLDialogElement): void {
  const popup = dialog as PopupDialog;
  if (popup._keydownHandler) {
    document.removeEventListener('keydown', popup._keydownHandler);
  }
  dialog.close();
  document.body.style.overflow = _savedBodyOverflow;
}

/**
 * Binds event handlers to popup elements and keyboard
 * @param {HTMLDialogElement} dialog - The dialog element
 * @param {Object} [options]
 * @param {boolean} [options.preview=false] - When true, uses closePopupPreview (no markSeen)
 */
export function bindEvents(
  dialog: HTMLDialogElement,
  { preview = false }: { preview?: boolean } = {},
): void {
  const close = preview ? closePopupPreview : closePopup;
  const popup = dialog as PopupDialog;

  const closeBtn = dialog.querySelector<HTMLElement>(SELECTORS.closeBtn);
  if (closeBtn) {
    closeBtn.addEventListener('click', () => {
      close(dialog);
    });
  }

  // Click on backdrop area (the dialog element itself, outside inner content)
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) {
      close(dialog);
    }
  });

  // Restore scrolling on native close
  dialog.addEventListener('close', () => {
    document.body.style.overflow = _savedBodyOverflow;
  });

  // Keyboard handler: Escape closes, Tab traps focus
  // Registered on document in openPopup(), removed in closePopup()
  const focusableEls = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE));
  popup._keydownHandler = (event: KeyboardEvent) => {
    if (!dialog.open) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      close(dialog);
      return;
    }

    if (event.key === 'Tab') {
      const focusable = focusableEls;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (!dialog.contains(document.activeElement)) {
        event.preventDefault();
        if (event.shiftKey) {
          last.focus();
        } else {
          first.focus();
        }
        return;
      }

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  };
}
